/**
 * Network > People
 *
 * The network-wide people list. Network lists partners, and a partner's page
 * lists the people on that one partner - so until this existed there was no way
 * to start from a person and find which partnership, head office or location,
 * they sit on.
 *
 * One row per person. The Offices column collapses a group: a granted head
 * office already covers every location under it, so the backend returns one
 * office for it rather than one row per location (see
 * services/partner_people.py). A person on several offices expands in place,
 * using the same chevron affordance a parent partner already has one tab over.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Users } from 'lucide-react'

import {
  DataTable,
  type ActiveFilter,
  type DataTableColumn,
} from '@/admin/components/data-table'
import { ListPageToolbar } from '@/admin/components/list-page-toolbar'
import {
  AccessBadge,
  OfficeName,
  OfficeTierBadge,
} from '@/admin/components/shared/partner-office'
import {
  ACCESS_SEVERITY,
  useAccessLabels,
} from '@/admin/components/shared/partner-office-access'
import { Button } from '@/shared/components/ui/button'
import { PageInfo } from '@/admin/components/page-info'
import { EmptyState } from '@/admin/components/shared/empty-state'
import type { StatusPillItem } from '@/admin/components/shared/status-pills'
import { useTableState } from '@/admin/hooks/use-table-state'
import {
  useNetworkPeople,
  useNetworkPeopleCounts,
  type NetworkPerson,
  type OfficeAccess,
} from '@/admin/hooks/use-network-people'
import { useFormatters } from '@/shared/lib/formatting'
import { cn } from '@/shared/lib/utils'
import type { TableViewConfig } from '@/admin/types/table-views'

const TABLE_ID = 'network-people'

/** Offices listed inline on expand before deferring to the person page. */
const MAX_INLINE_OFFICES = 6

/**
 * Semantic access state -> badge tone. The backend owns the state; the frontend
 * only decides how each one looks, so a new state cannot be introduced here by
 * accident.
 */
export function PartnerPeoplePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { formatDate } = useFormatters()

  const [search, setSearch] = useState('')
  const [accessFilter, setAccessFilter] = useState<string | null>(null)
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([])
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})
  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const { page, pageSize, sorting, setPage, setPageSize, setSorting, queryParams } =
    useTableState(TABLE_ID, { defaultSorting: { field: 'email', direction: 'asc' } })

  const params = useMemo(
    () => ({
      ...queryParams,
      ...(search ? { search } : {}),
      ...(accessFilter ? { access: accessFilter } : {}),
    }),
    [queryParams, search, accessFilter],
  )

  const { data, isLoading, isFetching, isError, refetch } = useNetworkPeople(params)
  const { data: counts } = useNetworkPeopleCounts()

  const people = data?.results ?? []
  const totalCount = data?.count ?? 0

  function toggleExpanded(uuid: string) {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })
  }

  const { accessLabel, pillLabel } = useAccessLabels()

  /** Open a partner from an expanded row, with Back pointing at this list. */
  const openPartner = (partnershipUuid: string) =>
    navigate(`/network/${partnershipUuid}`, {
      state: {
        backTo: '/network/people',
        backLabel: t('network.people.backToPeople', 'Back to people'),
      },
    })

  /**
   * The pills are FILTERS, not buckets: a person granted at one location and
   * stranded at another is counted under both, so these deliberately do not sum
   * to the All total.
   */
  const statusPills: StatusPillItem[] = useMemo(
    () =>
      counts
        ? (
            [
              'direct_grant',
              'inherited',
              'no_grant',
              'suspended',
              'orphaned_grant',
            ] as OfficeAccess[]
          )
            .map((state) => ({
              key: state,
              label: pillLabel(state),
              count: counts[state] ?? 0,
            }))
            .filter((pill) => pill.count > 0 || pill.key === accessFilter)
        : [],
    [counts, accessFilter, pillLabel],
  )

  const columns = useMemo<DataTableColumn<NetworkPerson>[]>(
    () => [
      {
        id: 'name',
        header: t('network.people.colName', 'Name'),
        sortable: true,
        sortField: 'first_name',
        cell: (row) => {
          return (
            <div className="flex items-center gap-2">
              {row.offices.length > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-expanded={expanded.has(row.uuid)}
                  aria-label={t('network.people.toggleOffices', 'Show offices')}
                  onClick={(event) => {
                    event.stopPropagation()
                    toggleExpanded(row.uuid)
                  }}
                  className="h-5 w-5 shrink-0 cursor-pointer rounded transition-colors duration-100 hover:bg-[var(--bg-subtle)]"
                >
                  <ChevronRight
                    className={cn(
                      'h-3.5 w-3.5 text-[var(--ink-3)] transition-transform duration-150',
                      expanded.has(row.uuid) && 'rotate-90',
                    )}
                  />
                </Button>
              ) : (
                <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                  <Users className="h-3.5 w-3.5 text-[var(--ink-3)]" />
                </div>
              )}
              <span className="truncate text-[13px] text-[var(--ink)]">
                {row.full_name || row.email}
              </span>
              {row.offices.length > 1 && (
                <span className="ml-1 shrink-0 rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] font-[500] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                  {row.offices.length}
                </span>
              )}
            </div>
          )
        },
      },
      {
        id: 'email',
        header: t('network.people.colEmail', 'Email'),
        sortable: true,
        sortField: 'email',
        cell: (row) => (
          <span className="text-[13px] text-[var(--ink-2)]">{row.email}</span>
        ),
      },
      {
        id: 'role',
        header: t('network.people.colRole', 'Partner role'),
        cell: (row) => {
          // A person on two businesses can hold two different partner roles;
          // showing the first one silently misreports the others.
          const roles = new Set(
            row.offices.map((office) => office.role_name).filter(Boolean),
          )
          const label =
            roles.size === 0
              ? '-'
              : roles.size === 1
                ? [...roles][0]
                : t('network.people.accessMixed', 'Mixed')
          return (
            <span className="text-[13px] text-[var(--ink-2)]">{label}</span>
          )
        },
      },
      {
        id: 'access',
        header: t('network.people.colAccess', 'Access'),
        cell: (row) => {
          if (row.offices.length === 0) return null
          // Surface the office that needs attention, not the fact that they
          // differ. "Mixed" is honest and useless: this column exists so an
          // admin can spot a problem without expanding the row, and a person
          // stranded at one of three offices IS the problem.
          const worst = [...row.offices].sort(
            (a, b) => ACCESS_SEVERITY[b.access] - ACCESS_SEVERITY[a.access],
          )[0]
          // Count by the LABEL shown, not by the raw state. Two states render
          // as "Has access" (a plain grant and one whose staff record is
          // missing), so counting states said "2 of 3" while every row on
          // screen read "Has access" - the qualifier contradicted the badge.
          const worstLabel = accessLabel(worst.access)
          const sameLabel = row.offices.filter(
            (office) => accessLabel(office.access) === worstLabel,
          ).length
          return (
            <span className="flex items-center gap-1.5">
              <AccessBadge state={worst.access} label={worstLabel} />
              {sameLabel < row.offices.length && (
                <span className="whitespace-nowrap text-[10px] tracking-[0.04em] text-[var(--ink-3)]">
                  {t('network.people.ofOffices', '{{count}} of {{total}}', {
                    count: sameLabel,
                    total: row.offices.length,
                  })}
                </span>
              )}
            </span>
          )
        },
      },
      {
        id: 'last_login',
        header: t('network.people.colLastActive', 'Last active'),
        sortable: true,
        sortField: 'last_login',
        cell: (row) => (
          <span
            className={cn(
              'whitespace-nowrap text-[13px]',
              // On a freshly migrated tenant most people have never signed in,
              // so the absent case recedes rather than filling the column.
              row.last_login ? 'text-[var(--ink-2)]' : 'text-[var(--ink-3)]',
            )}
          >
            {row.last_login
              ? formatDate(row.last_login)
              : t('network.people.never', 'Never')}
          </span>
        ),
      },
    ],
    [t, expanded, formatDate, accessLabel],
  )

  const currentConfig: TableViewConfig = {
    columns: columns.map((column) => column.id),
    sorting: sorting ?? null,
    filters: [],
  }

  return (
    <div>
      {/* Page header */}
      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="clara-heading">{t('network.people.title', 'People')}</h1>
          <div className="mt-1 flex items-center gap-2">
            <p className="clara-small">
              {t(
                'network.people.subtitle',
                'Everyone who reaches the platform through a partner.',
              )}
            </p>
            <PageInfo>
              {t(
                'network.people.description',
                'Being on an office staff list makes someone eligible for access; it does not give it to them. Access is what lets them sign in and act for that office. A head office with access already covers every location under it, so those locations are not listed separately.',
              )}
            </PageInfo>
          </div>
        </div>
      </div>

      <ListPageToolbar<NetworkPerson>
        tableId={TABLE_ID}
        layout="compact"
        search={search}
        onSearchChange={(value) => {
          setSearch(value)
          setPage(1)
        }}
        searchPlaceholder={t('network.people.searchPlaceholder', 'Search people...')}
        statusPills={statusPills}
        activeStatusKey={accessFilter}
        onStatusSelect={(key) => {
          setAccessFilter(key)
          setPage(1)
        }}
        statusTotalCount={counts?.all ?? totalCount}
        views={[]}
        showViews={false}
        activeViewUuid={null}
        onSelectView={() => {}}
        onSaveView={() => {}}
        onDeleteView={() => {}}
        onRenameView={() => {}}
        currentConfig={currentConfig}
        columns={columns}
        activeFilters={activeFilters}
        onFiltersChange={(filters) => {
          setActiveFilters(filters)
          setPage(1)
        }}
        showColumnFilter={false}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
      />

      <DataTable<NetworkPerson>
        tableId={TABLE_ID}
        columns={columns}
        data={people}
        totalCount={totalCount}
        // Both flags: isLoading alone lets a cached-empty page flash the empty
        // state while the refetch is still in flight.
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        onRetry={refetch}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
        sorting={sorting}
        onSortingChange={(next) => {
          setSorting(next)
          setPage(1)
        }}
        columnVisibility={columnVisibility}
        columnOrder={columnOrder}
        highlightQuery={search}
        rowKey={(row) => row.uuid}
        onRowClick={(row) => navigate(`/network/people/${row.uuid}`)}
        rowTestId={(row) => `network-people-item-${row.uuid}`}
        renderRowDetail={(row) => {
          if (!expanded.has(row.uuid)) return null
          // A triage panel, not a dump. Everything here already rides the row
          // payload, so this costs no request - the point is that an admin can
          // work through several people without opening each one.
          //
          // Role and Access are only shown when the person has MORE than one
          // office: with one they cannot differ from the columns above, so
          // repeating them says nothing. What the row can never show is which
          // office, and since when.
          // A column that can only say one thing on this row set is not
          // information. Where every office has the same access, the row's own
          // badge above already said it - repeating it per office states the
          // tautology "these are the offices she is on, and she is on them".
          // Access still appears the moment it DIFFERS, which is the case worth
          // seeing: on staff at one office and unable to sign in at another.
          const varies = (pick: (o: (typeof row.offices)[number]) => string) =>
            new Set(row.offices.map(pick)).size > 1
          const showAccess = varies((office) => accessLabel(office.access))
          const showRole = varies((office) => office.role_name ?? '-')
          const shown = row.offices.slice(0, MAX_INLINE_OFFICES)
          const hidden = row.offices.length - shown.length
          const stranded = row.offices.filter(
            (office) => office.access === 'no_grant',
          ).length
          const redundant = row.offices.reduce(
            (total, office) => total + office.redundant_grant_count,
            0,
          )
          // Content-width columns, left-aligned. Stretching these to the table
          // width left a dead gap between the office and its dates, so the two
          // halves of one fact read as unrelated.
          const grid =
            showRole && showAccess
              ? 'grid-cols-[minmax(0,22rem)_minmax(0,8rem)_auto_8rem_7rem]'
              : showRole
                ? 'grid-cols-[minmax(0,22rem)_minmax(0,8rem)_8rem_7rem]'
                : showAccess
                  ? 'grid-cols-[minmax(0,22rem)_auto_8rem_7rem]'
                  : 'grid-cols-[minmax(0,22rem)_8rem_7rem]'

          return (
            <div className="panel-arrive flex flex-col gap-1.5 py-1">
              {((stranded > 0 && stranded < row.offices.length) ||
                redundant > 0) && (
                <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                  {stranded > 0 && stranded < row.offices.length && (
                    <span className="text-[var(--status-error)]">
                      {t('network.people.strandedCount', 'No access: {{count}}', {
                        count: stranded,
                      })}
                    </span>
                  )}
                  {redundant > 0 && (
                    <span>
                      {t('network.people.redundantCount', 'Duplicate access: {{count}}', {
                        count: redundant,
                      })}
                    </span>
                  )}
                </div>
              )}

              {/* Micro-header so the values below line up and need no inline
                  labels - the prose form ("member since X, granted Y") read as
                  a sentence stuffed into a table. */}
              <div className={cn('grid items-center gap-x-4 gap-y-1', grid)}>
                <span className="clara-th">
                  {t('network.people.hdrOffice', 'Office')}
                </span>
                {showRole && (
                  <span className="clara-th">
                    {t('network.people.colRole', 'Partner role')}
                  </span>
                )}
                {showAccess && (
                  <span className="clara-th">
                    {t('network.people.colAccess', 'Access')}
                  </span>
                )}
                <span className="clara-th">
                  {t('network.people.hdrMemberSince', 'On staff since')}
                </span>
                <span className="clara-th">
                  {t('network.people.hdrGranted', 'Access since')}
                </span>
              </div>

              {shown.map((office) => (
                <div
                  key={office.partnership_uuid}
                  className={cn(
                    'grid items-center gap-x-4 gap-y-1 text-[13px]',
                    grid,
                  )}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <OfficeName office={office} onOpen={openPartner} />
                    <OfficeTierBadge office={office} />
                  </span>
                  {showRole && (
                    <span className="truncate text-[var(--ink-2)]">
                      {office.role_name ?? '-'}
                    </span>
                  )}
                  {showAccess && (
                    <AccessBadge
                      state={office.access}
                      label={accessLabel(office.access)}
                    />
                  )}
                  <span
                    className={cn(
                      'whitespace-nowrap',
                      office.member_since
                        ? 'text-[var(--ink-2)]'
                        : 'text-[var(--status-warn)]',
                    )}
                  >
                    {office.member_since
                      ? formatDate(office.member_since)
                      : t('network.person.notAMember', 'Not on staff')}
                  </span>
                  <span className="whitespace-nowrap text-[var(--ink-2)]">
                    {office.granted_at ? formatDate(office.granted_at) : '-'}
                  </span>
                </div>
              ))}

              <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.1em]">
                {hidden > 0 && (
                  <span className="text-[var(--ink-3)]">
                    {t('network.people.andMoreOffices', 'and {{count}} more', {
                      count: hidden,
                    })}
                  </span>
                )}
                <Button
                  variant="ghost"
                  onClick={(event) => {
                    event.stopPropagation()
                    navigate(`/network/people/${row.uuid}`)
                  }}
                  className="h-auto px-0 text-[11px] uppercase tracking-[0.1em] text-[var(--green)] hover:bg-transparent hover:text-[var(--green-hover)]"
                >
                  {t('network.people.viewFullDetails', 'View full details')}
                </Button>
              </div>
            </div>
          )
        }}
        renderRowDetailStartColumn={1}
        highlightExpandedDetail
        emptyStateNode={
          search || accessFilter ? (
            <EmptyState
              icon={Users}
              title={t('network.people.noMatches', 'No people match')}
              description={t(
                'network.people.noMatchesDescription',
                'Try a different name or email, or clear the access filter.',
              )}
            />
          ) : (
            <EmptyState
              icon={Users}
              title={t('network.people.emptyState', 'No partner people yet')}
              description={t(
                'network.people.emptyStateDescription',
                'People appear here once a partner has someone with access or an open invitation.',
              )}
            />
          )
        }
      />
    </div>
  )
}
